import React, { useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Typography, LinearProgress, Box, Grid } from '@mui/material';
import { useDispatch } from 'react-redux';

import PageTemplate from 'src/components/PageTemplate';
import ProcessingCard from './components/ProcessingCard';
import QueryDetailsCard from './components/QueryDetailsCard';
import PitchCard from './components/PitchCard';
import BackButton from 'src/components/BackButton';
import useQueryDetails from 'src/hooks/useQueryDetails';
import useUpdateQueryStatus from 'src/hooks/useUpdateQueryStatus';
import { useDecodedToken } from 'src/hooks/useDecodedToken';
import { setActiveStatus } from 'src/store/slices/navbarSlice';
import { QUERY_STATUSES, PATHS } from 'src/constants/config';
import { useExpertManagement } from 'src/hooks/useExpertManagement';
import { findExpertFromQuery } from 'src/utils/query';
import { queryApi } from 'src/api/queryApi';

const QueryProfile = () => {
  const { queryID } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { decodedToken, isExpert } = useDecodedToken();
  const dispatch = useDispatch();

  const { loading, isProcessing, query, refetch, queueProgress } = useQueryDetails(queryID);
  const { updateStatus } = useUpdateQueryStatus(queryID, decodedToken?.userID);
  const { experts, isLoading: expertsLoading } = useExpertManagement();

  useEffect(() => {
    if (query?.status) {
      dispatch(setActiveStatus(query.status));
    }
    return () => dispatch(setActiveStatus(null));
  }, [dispatch, query?.status]);

  // Automatically set expertsID if it's null
  useEffect(() => {
    const setExpertsID = async () => {
      // Only proceed if we have query data, experts, and expertsID is null
      if (!query || !experts || expertsLoading || query.expertsID !== null || query.status === 'draft' || query.status === 'new') {
        return;
      }

      // Find the expert using the utility function
      const foundExpert = findExpertFromQuery(experts, query);

      if (foundExpert) {
        try {
          // Update the query with the found expert's ID
          await queryApi.updateQueryV2({
            queryID: query.id,
            title: query.title,
            description: query.description,
            status: query.status,
            source: query.source,
            topics: query.topics,
            expertsID: foundExpert.id,
          });

          // Refetch the query to get the updated data
          refetch();
        } catch (error) {
          // Silently handle error - expertsID update failed
        }
      }
    };

    setExpertsID();
  }, [query, experts, expertsLoading, refetch]);

  const getBackPath = () => {
    const basePath = QUERY_STATUSES[query?.status]?.path || PATHS.PITCH_INBOX.value;

    // Get current search params but exclude the status parameter to avoid duplication
    const currentParams = new URLSearchParams(searchParams);
    currentParams.delete('status'); // Remove status since it's already in basePath

    const additionalParams = currentParams.toString();
    if (!additionalParams) {
      return basePath;
    }

    // Check if basePath already has query parameters
    const hasQueryParams = basePath.includes('?');
    const separator = hasQueryParams ? '&' : '?';

    return `${basePath}${separator}${additionalParams}`;
  };

  const handleDiscardQuery = async () => {
    const success = await updateStatus('discarded');
    if (success) {
      navigate(getBackPath());
    }
  };

  const marginTop = isExpert ? { xs: 3, sm: 3 } : { xs: 10, sm: 4 };

  return (
    <PageTemplate loading={loading}>
      {loading ? (
        <Box sx={{ width: '100%', mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Loading Query...
          </Typography>
          <LinearProgress sx={{ height: 8, borderRadius: 4 }} />
        </Box>
      ) : query ? (
        <Box sx={{ mt: marginTop }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={12}>
              <BackButton path={getBackPath()} />
            </Grid>
            <Grid item xs={12} md={4}>
              <QueryDetailsCard query={query} handleDiscardQuery={handleDiscardQuery} refetch={refetch} />
            </Grid>
            <Grid item xs={12} md={8} sx={{ pl: 4 }}>
              {isProcessing ? <ProcessingCard queueProgress={queueProgress} /> : <PitchCard query={query} refetch={refetch} />}
            </Grid>
          </Grid>
        </Box>
      ) : (
        <Typography variant="h5">Query not found</Typography>
      )}
    </PageTemplate>
  );
};

export default QueryProfile;
